import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { OrganizationSchema, WebsiteSchema, FAQSchema } from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { comics, getHotComics, getNewComics, Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[3/4] overflow-hidden relative">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 免费韩漫在线漫画`}
          title={`${comic.title} - 无删减下拉式韩漫免费阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          width={600}
          height={800}
        />
        {comic.status === '连载中' && (
          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
            连载中
          </span>
        )}
        {comic.status === '已完结' && (
          <span className="absolute top-2 left-2 bg-secondary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
            已完结
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          <div className="flex items-center gap-1 text-xs text-white/90">
            <i className="ri-star-fill text-amber-400"></i>
            <span>{comic.rating}</span>
            <span className="text-white/60">({(comic.ratingCount / 10000).toFixed(1)}万评)</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{comic.summary}</p>
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {comic.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-[11px] text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

const homeFAQs = [
  {
    question: '韩漫漫画网站上的漫画都是免费的吗？',
    answer: '是的，韩漫漫画网站推荐的所有韩漫资源均支持免费在线阅读，采用无删减下拉式阅读模式，为您提供流畅的韩漫免费阅读体验。我们持续收录优质免费韩漫资源，更新及时。',
  },
  {
    question: '什么是无删减下拉式韩漫免费阅读？',
    answer: '无删减下拉式韩漫免费阅读是一种优化的在线阅读模式。漫画内容保持原版完整性，不做任何删减处理。阅读时只需向下滚动即可连续观看，无需翻页点击，提供沉浸式的韩漫阅读体验。',
  },
  {
    question: '韩漫漫画网站有哪些类型的漫画？',
    answer: '韩漫漫画网站涵盖了恋爱、热血动作、奇幻冒险、悬疑惊悚、轻松搞笑、都市剧情、BL 专区、古风穿越等多种类型的免费韩漫资源，满足不同读者的阅读偏好。无论您喜欢哪种风格，都能在这里找到心仪的在线漫画。',
  },
  {
    question: '漫画更新频率是怎样的？',
    answer: '我们推荐的韩漫资源更新非常及时，热门连载漫画通常每周更新一话，部分作品甚至有多平台同步更新的优势。建议收藏本网站，随时获取最新的免费韩漫在线漫画更新信息。',
  },
  {
    question: '如何找到好看的韩漫推荐？',
    answer: '您可以通过首页的"热门推荐"区查看评分最高的韩漫作品，也可以使用"分类浏览"功能按照不同类型筛选漫画。每部漫画都附有详细评分、标签和简介，帮助您快速找到感兴趣的免费韩漫。',
  },
];

const seoTitle = '免费韩漫在线漫画网站推荐大全_无删减下拉式韩漫免费阅读-韩漫漫画网站';
const seoDesc =
  '韩漫漫画网站汇集免费韩漫在线漫画网站推荐大全，支持无删减下拉式韩漫免费阅读，资源优质更新及时。涵盖恋爱、热血、奇幻、悬疑等多种类型，为您提供最全的韩漫推荐与阅读指南。';
const seoKeywords = '免费韩漫,在线漫画网站,推荐大全,无删减下拉式,韩漫免费阅读';

export default function Home() {
  const hotComics = getHotComics();
  const newComics = getNewComics();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20webtoon%20style%20collage%20with%20various%20manhwa%20characters%2C%20colorful%20and%20vibrant%2C%20warm%20atmosphere%2C%20comic%20book%20aesthetic%2C%20professional%20layout&width=1200&height=630&seq=og-home&orientation=landscape"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <FAQSchema faqs={homeFAQs} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20Korean%20webtoon%20illustration%20wall%20with%20vibrant%20warm%20colors%20and%20dynamic%20character%20silhouettes%2C%20comic%20book%20style%20background%20with%20soft%20gradient%20overlay%2C%20modern%20Korean%20digital%20art%20aesthetic%2C%20clean%20composition%2C%20warm%20coral%20and%20gold%20accents%2C%20no%20text&width=1600&height=900&seq=hero-hanman-home&orientation=landscape"
            alt="免费韩漫在线漫画 - 无删减下拉式韩漫免费阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
              免费韩漫在线漫画网站推荐大全
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              汇集优质免费韩漫资源，支持<strong>无删减下拉式韩漫免费阅读</strong>，
              涵盖恋爱、热血、奇幻、悬疑等多元类型。更新及时，推荐精准，发现你的下一部心头好韩漫。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/love"
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-white px-8 py-3.5 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                立即探索韩漫
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto bg-white/15 hover:bg-white/25 text-white px-8 py-3.5 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-10">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">500+</div>
                <div className="text-xs text-white/60 mt-1">推荐漫画</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">8</div>
                <div className="text-xs text-white/60 mt-1">精选分类</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">99%</div>
                <div className="text-xs text-white/60 mt-1">好评率</div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-2">
              分类浏览
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              按喜好探索，精准找到你想看的韩漫类型
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">{cat.count} 部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">评分最高的人气韩漫精选</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {hotComics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.slug} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最近上线的优质韩漫新作</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {newComics.concat(hotComics.filter((c) => !c.isNew)).slice(0, 6).map((comic) => (
                <ComicCard key={comic.slug} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* All Comics Grid */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  全部推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">发现更多精彩韩漫作品</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {comics.slice(0, 12).map((comic) => (
                <ComicCard key={comic.slug} comic={comic} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/category/love"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部漫画
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              关于免费韩漫阅读的常见疑问解答
            </p>
            <div className="space-y-3">
              {homeFAQs.map((faq, index) => (
                <details key={index} className="bg-background-50 rounded-lg border border-background-200/70 group">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-700 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
              准备好开始你的韩漫之旅了吗？
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              数百部精选韩漫等你来发现，免费、无删减、高品质阅读体验
            </p>
            <Link
              to="/category/love"
              className="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-8 py-3.5 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              立即探索
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}